/**
 * The header's bell: the number of unread notices, and a panel with the latest ones. Opening the
 * panel marks every notice read. The panel closes on a choice, on Escape, on a click elsewhere
 * and when the page changes. On narrow screens the header shows `NotificationLink` instead, which
 * leads to the notifications page.
 */

import { Bell } from "lucide-react";
import { useEffect, useId, useRef, useState } from "react";
import { Link, useLocation } from "react-router";

import { focusRing } from "../components/ui.js";
import { NotificationItem } from "./NotificationItem.js";
import { BELL_NOTICES, useMarkNotificationsRead, useNotifications } from "./notifications.js";

/** The count on the bell. More than 99 reads "99+". */
function Count({ unread }: { unread: number }) {
  return (
    <span
      aria-hidden
      className="absolute -top-0.5 -right-0.5 flex h-[1.125rem] min-w-[1.125rem] items-center justify-center rounded-full bg-brand-600 px-1 text-[0.6875rem] leading-none font-semibold text-white tabular-nums ring-2 ring-white dark:bg-brand-400 dark:text-ink-950 dark:ring-ink-950"
    >
      {unread > 99 ? "99+" : unread}
    </span>
  );
}

/** What a screen reader says for the bell. */
function label(unread: number | undefined): string {
  return unread ? `Notifications, ${unread} unread` : "Notifications";
}

const bellButton = `relative rounded-full p-2 text-zinc-600 transition-colors hover:bg-zinc-900/[0.05] hover:text-zinc-950 aria-expanded:bg-zinc-900/[0.06] aria-expanded:text-zinc-950 dark:text-zinc-400 dark:hover:bg-white/[0.07] dark:hover:text-white dark:aria-expanded:bg-white/[0.08] dark:aria-expanded:text-white ${focusRing}`;

export function NotificationBell() {
  const list = useNotifications(BELL_NOTICES);
  const { markRead, refresh } = useMarkNotificationsRead();
  const [open, setOpen] = useState(false);
  const container = useRef<HTMLDivElement>(null);
  const button = useRef<HTMLButtonElement>(null);
  const panelId = useId();
  const { pathname } = useLocation();
  const unread = list.data?.unread ?? 0;

  /** Closes the panel. The list is then read again, so that what was read loses its mark. */
  function close() {
    setOpen(false);
    void refresh();
  }

  function toggle() {
    if (open) {
      close();
      return;
    }
    setOpen(true);
    if (unread > 0) {
      markRead();
    }
  }

  // biome-ignore lint/correctness/useExhaustiveDependencies: closes the panel on every page change.
  useEffect(() => {
    setOpen(false);
  }, [pathname]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: `close` is made anew on every render.
  useEffect(() => {
    if (!open) {
      return;
    }
    function onPointerDown(event: PointerEvent) {
      if (!container.current?.contains(event.target as Node)) {
        close();
      }
    }
    function onKeyDown(event: KeyboardEvent) {
      if (event.key === "Escape") {
        close();
        button.current?.focus();
      }
    }
    document.addEventListener("pointerdown", onPointerDown);
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("pointerdown", onPointerDown);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [open]);

  // Nothing is shown until a wallet is signed in and its notices are known.
  if (!list.data) {
    return null;
  }
  const notices = list.data.notifications;

  return (
    <div ref={container} className="relative">
      <button
        ref={button}
        type="button"
        aria-label={label(unread)}
        aria-expanded={open}
        aria-controls={panelId}
        onClick={toggle}
        className={bellButton}
      >
        <Bell aria-hidden className="size-5" />
        {unread > 0 && <Count unread={unread} />}
      </button>
      {open && (
        <section
          id={panelId}
          aria-label="Notifications"
          className="absolute right-0 z-40 mt-2 w-[24rem] max-w-[calc(100vw-2rem)] rounded-2xl bg-white p-2 text-zinc-900 shadow-xl ring-1 shadow-zinc-900/10 ring-zinc-900/10 dark:bg-ink-850 dark:text-zinc-100 dark:shadow-black/50 dark:ring-white/10"
        >
          <h2 className="px-3 pt-2 pb-1 font-display text-sm font-semibold text-zinc-950 dark:text-white">
            Notifications
          </h2>
          {notices.length === 0 ? (
            <p className="px-3 pt-1 pb-3 text-sm text-zinc-600 dark:text-zinc-400">
              Nothing yet. Morcast tells you here when something happens to your campaigns, your
              items or your money.
            </p>
          ) : (
            <ul className="max-h-[26rem] overflow-y-auto">
              {notices.map((notice) => (
                <li key={notice.id}>
                  <NotificationItem notice={notice} compact onOpen={close} />
                </li>
              ))}
            </ul>
          )}
          <div className="mt-1 border-t border-zinc-900/[0.07] pt-1 dark:border-white/10">
            <Link
              to="/notifications"
              onClick={close}
              className="block rounded-lg px-3 py-2 text-center text-sm font-medium text-brand-700 transition-colors hover:bg-zinc-900/[0.04] dark:text-brand-300 dark:hover:bg-white/[0.05]"
            >
              See all notifications
            </Link>
          </div>
        </section>
      )}
    </div>
  );
}

/** The bell on narrow screens: the count, as a link to the notifications page. */
export function NotificationLink() {
  const list = useNotifications(BELL_NOTICES);
  if (!list.data) {
    return null;
  }
  const { unread } = list.data;
  return (
    <Link to="/notifications" aria-label={label(unread)} className={bellButton}>
      <Bell aria-hidden className="size-5" />
      {unread > 0 && <Count unread={unread} />}
    </Link>
  );
}
